import type {
  ReviewAverageByCategoryResponse,
  ReviewDistributionResponse,
  ReviewOverallResponse,
  ReviewOverTimeResponse,
  ReviewSourceSplitResponse,
} from "@/types/analytics-reviews";
import { REVIEWS_COLORS } from "./constants";
import type {
  CategoryKey,
  RatingTrendPoint,
  ReviewsSourceSplitView,
  ReviewsSummary,
  StarDistributionSegment,
} from "./types";

const STAR_LABELS: Record<number, string> = {
  5: "5 stars",
  4: "4 stars",
  3: "3 stars",
  2: "2 stars",
  1: "1 star",
};

const STAR_COLOR_BY_VALUE: Record<number, string> = {
  5: REVIEWS_COLORS.star5,
  4: REVIEWS_COLORS.star4,
  3: REVIEWS_COLORS.star3,
  2: REVIEWS_COLORS.star2,
  1: REVIEWS_COLORS.star1,
};

const DEFAULT_CATEGORY_KEYS: CategoryKey[] = ["purity", "comfort", "service"];

function isCategoryKey(value: string): value is CategoryKey {
  return DEFAULT_CATEGORY_KEYS.includes(value as CategoryKey);
}

export function mapOverallToSummary(
  response: ReviewOverallResponse | undefined,
): ReviewsSummary {
  return {
    overallRating: response?.overallRating ?? null,
    positiveNeutralPercent: response?.positiveNeutralPercent ?? null,
    negativePercent: response?.negativePercent ?? null,
    reviewsCount: response?.reviewsCount ?? 0,
  };
}

export function mapDistributionToSegments(
  response: ReviewDistributionResponse | undefined,
  emptyFallback: StarDistributionSegment[],
): StarDistributionSegment[] {
  if (!response?.buckets.length) {
    return emptyFallback;
  }

  return [...response.buckets]
    .sort((left, right) => right.star - left.star)
    .map((bucket) => ({
      key: `star${bucket.star}`,
      name: STAR_LABELS[bucket.star] ?? `${bucket.star} stars`,
      value: bucket.count,
      fill: STAR_COLOR_BY_VALUE[bucket.star] ?? REVIEWS_COLORS.star3,
    }));
}

export function mapSourceSplit(
  response: ReviewSourceSplitResponse | undefined,
): ReviewsSourceSplitView | null {
  if (!response?.sourceSplitSupported) {
    return null;
  }

  return {
    preCheckoutCount: response.preCheckoutCount,
    postStayCount: response.postStayCount,
    total: response.total,
    note: response.note,
  };
}

export function mapAverageByCategory(
  response: ReviewAverageByCategoryResponse | undefined,
): {
  categoriesSupported: boolean;
  categoryKeys: CategoryKey[];
  categoryAverages: Partial<Record<CategoryKey, number>>;
} {
  const categoriesSupported = response?.categoriesSupported ?? false;

  if (!categoriesSupported) {
    return {
      categoriesSupported: false,
      categoryKeys: [],
      categoryAverages: {},
    };
  }

  if (!response?.categories.length) {
    return {
      categoriesSupported: true,
      categoryKeys: DEFAULT_CATEGORY_KEYS,
      categoryAverages: {},
    };
  }

  const categoryAverages: Partial<Record<CategoryKey, number>> = {};
  const dynamicKeys: string[] = [];

  for (const item of response.categories) {
    dynamicKeys.push(item.category);
    if (isCategoryKey(item.category) && item.avgRating != null) {
      categoryAverages[item.category] = item.avgRating;
    }
  }

  const categoryKeys = DEFAULT_CATEGORY_KEYS.filter((key) =>
    dynamicKeys.includes(key),
  );

  for (const key of dynamicKeys) {
    if (isCategoryKey(key) && !categoryKeys.includes(key)) {
      categoryKeys.push(key);
    }
  }

  return {
    categoriesSupported: true,
    categoryKeys,
    categoryAverages,
  };
}

export function mapOverTimeToRatingTrend(
  response: ReviewOverTimeResponse | undefined,
): {
  categoriesSupported: boolean;
  categoryKeys: CategoryKey[];
  ratingTrend: RatingTrendPoint[];
} {
  const categoriesSupported = response?.categoriesSupported ?? false;
  const series = response?.series ?? [];

  const categoryKeys = categoriesSupported ? collectCategoryKeys(series) : [];

  const ratingTrend = series.map((row) => {
    const point: RatingTrendPoint = {
      day: row.day,
      overallRating: row.overallRating,
    };

    if (categoriesSupported && row.categories) {
      for (const key of categoryKeys) {
        const value = row.categories[key];
        if (value != null) {
          point[key] = value;
        }
      }
    }

    return point;
  });

  return {
    categoriesSupported,
    categoryKeys,
    ratingTrend,
  };
}

function collectCategoryKeys(
  series: ReviewOverTimeResponse["series"],
): CategoryKey[] {
  const keys = new Set<string>();

  for (const row of series) {
    if (!row.categories) {
      continue;
    }

    for (const key of Object.keys(row.categories)) {
      keys.add(key);
    }
  }

  const ordered = DEFAULT_CATEGORY_KEYS.filter((key) => keys.has(key));

  for (const key of keys) {
    if (!ordered.includes(key as CategoryKey) && isCategoryKey(key)) {
      ordered.push(key);
    }
  }

  return ordered;
}
